import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBolt } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ActionStatusChip, actionHintKey } from "./actionParts";
import { type ActionDetailPayload, parseActionDetailPayload } from "./chiefSchema";

interface ActionDetailRendererProps {
  block: StreamingBlock;
}

export const ActionDetailRenderer = (props: ActionDetailRendererProps) => {
  const { t } = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ActionDetailPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseActionDetailPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("chief_actions_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="flex items-center gap-2 px-3 py-2">
              <TbBolt class="size-4 shrink-0 text-brand" />
              <span class="min-w-0 truncate text-sm font-medium text-primary-text">{data().action.name}</span>
              <span class="ml-auto shrink-0">
                <ActionStatusChip enabled={data().action.enabled} />
              </span>
            </div>
            <Show when={data().action.description || actionHintKey(data().action)}>
              <div class="px-3 pb-2">
                <Show when={data().action.description}>
                  <p class="text-xs text-muted-text">{data().action.description}</p>
                </Show>
                <Show when={actionHintKey(data().action)}>
                  {(key) => <p class="mt-0.5 text-xs text-muted-text">{t(key())}</p>}
                </Show>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
